import { Injectable, Injector } from "@angular/core";
import {
  HttpClient,
  HttpResponse as AngularHttpResponse,
} from "@angular/common/http";
import { Observable, from } from "rxjs";
import { map } from "rxjs/operators";
import { CapacitorHttp, HttpOptions, HttpResponse } from "@capacitor/core";
import { Capacitor } from "@capacitor/core";
import { AuthenticationService } from "./authentication.service";

@Injectable({
  providedIn: "root",
})
export class NativeHttpService {
  private authService?: AuthenticationService;

  constructor(private http: HttpClient, private injector: Injector) {}

  private getAuthService(): AuthenticationService {
    if (!this.authService) {
      this.authService = this.injector.get(AuthenticationService);
    }
    return this.authService;
  }

  private addAuthHeaders(headers: any): any {
    const authService = this.getAuthService();
    const currentUser = authService.currentUserValue;
    const enhancedHeaders = { ...headers };

    console.log(
      "addAuthHeaders - currentUser:",
      currentUser ? "found" : "null"
    );
    console.log(
      "addAuthHeaders - jwtToken:",
      currentUser?.jwtToken ? "present" : "missing"
    );

    if (currentUser && currentUser.jwtToken) {
      enhancedHeaders["Authorization"] = `Bearer ${currentUser.jwtToken}`;
      console.log("addAuthHeaders - Authorization header added");
    } else {
      console.warn("addAuthHeaders - No JWT token available!");
    }

    return enhancedHeaders;
  }

  get<T>(url: string, options?: any): Observable<T> {
    if (Capacitor.isNativePlatform()) {
      const httpOptions: HttpOptions = {
        url: url,
        headers: this.addAuthHeaders(options?.headers || {}),
        params: options?.params || {},
        webFetchExtra: {
          credentials: "include",
        },
      };

      console.log("CapacitorHttp GET request:", {
        url: url,
        headers: httpOptions.headers,
        credentials: "include",
        hasAuthHeader: !!(
          httpOptions.headers && httpOptions.headers["Authorization"]
        ),
      });

      return from(
        CapacitorHttp.get(httpOptions)
          .then((response: HttpResponse) => {
            console.log("CapacitorHttp GET response:", {
              url: url,
              status: response.status,
              dataType: typeof response.data,
              isArray: Array.isArray(response.data),
              data: response.data,
            });

            if (response.status >= 400) {
              console.error("CapacitorHttp GET error response:", response);
              throw new Error(`HTTP ${response.status}: ${response.data}`);
            }

            return response.data as T;
          })
          .catch((error) => {
            console.error("CapacitorHttp GET error:", {
              url: url,
              error: error,
              message: error.message,
            });
            throw error;
          })
      );
    } else {
      return this.http.get(url, options) as Observable<T>;
    }
  }

  post<T>(url: string, body: any, options?: any): Observable<T> {
    if (Capacitor.isNativePlatform()) {
      const baseHeaders = {
        "Content-Type": "application/json",
        ...(options?.headers || {}),
      };
      const httpOptions: HttpOptions = {
        url: url,
        headers: this.addAuthHeaders(baseHeaders),
        data: body,
        webFetchExtra: {
          credentials: "include",
        },
      };

      console.log("CapacitorHttp POST request:", {
        url: url,
        headers: httpOptions.headers,
        contentType: httpOptions.headers?.["Content-Type"] || "missing",
        hasAuth: !!(
          httpOptions.headers && httpOptions.headers["Authorization"]
        ),
        bodyKeys: Object.keys(body || {}),
      });

      return from(
        CapacitorHttp.post(httpOptions)
          .then((response: HttpResponse) => {
            console.log("CapacitorHttp POST response:", {
              url: url,
              status: response.status,
              headers: response.headers,
            });

            if (response.status >= 400) {
              console.error("CapacitorHttp POST error response:", response);
              throw new Error(`HTTP ${response.status}: ${response.data}`);
            }

            return response.data as T;
          })
          .catch((error) => {
            console.error("CapacitorHttp POST error:", {
              url: url,
              error: error,
              message: error.message,
            });
            throw error;
          })
      );
    } else {
      return this.http.post(url, body, options) as Observable<T>;
    }
  }

  put<T>(url: string, body: any, options?: any): Observable<T> {
    if (Capacitor.isNativePlatform()) {
      const baseHeaders = {
        "Content-Type": "application/json",
        ...(options?.headers || {}),
      };
      const httpOptions: HttpOptions = {
        url: url,
        headers: this.addAuthHeaders(baseHeaders),
        data: body,
        webFetchExtra: {
          credentials: "include",
        },
      };

      console.log("CapacitorHttp PUT request:", {
        url: url,
        headers: httpOptions.headers,
        bodyKeys: Object.keys(body || {}),
      });

      return from(
        CapacitorHttp.put(httpOptions)
          .then((response: HttpResponse) => {
            console.log("CapacitorHttp PUT response:", {
              url: url,
              status: response.status,
            });

            if (response.status >= 400) {
              console.error("CapacitorHttp PUT error response:", response);
              throw new Error(`HTTP ${response.status}: ${response.data}`);
            }

            return response.data as T;
          })
          .catch((error) => {
            console.error("CapacitorHttp PUT error:", {
              url: url,
              error: error,
              message: error.message,
            });
            throw error;
          })
      );
    } else {
      return this.http.put(url, body, options) as Observable<T>;
    }
  }

  delete<T>(url: string, options?: any): Observable<T> {
    if (Capacitor.isNativePlatform()) {
      const httpOptions: HttpOptions = {
        url: url,
        headers: this.addAuthHeaders(options?.headers || {}),
        webFetchExtra: {
          credentials: "include",
        },
      };
      return from(
        CapacitorHttp.delete(httpOptions).then((response: HttpResponse) => {
          if (options?.observe === "response") {
            return { status: response.status, body: response.data } as T;
          }
          return response.data as T;
        })
      );
    } else {
      const httpOptions: any = { ...options };
      if (options?.responseType === "text") {
        httpOptions.responseType = "text" as const;
      }
      if (options?.observe === "response") {
        httpOptions.observe = "response" as const;
      }
      return this.http.delete(url, httpOptions) as Observable<T>;
    }
  }

  patch<T>(url: string, body: any, options?: any): Observable<T> {
    if (Capacitor.isNativePlatform()) {
      const baseHeaders = {
        "Content-Type": "application/json",
        ...(options?.headers || {}),
      };
      const httpOptions: HttpOptions = {
        url: url,
        headers: this.addAuthHeaders(baseHeaders),
        data: body,
        webFetchExtra: {
          credentials: "include",
        },
      };
      return from(
        CapacitorHttp.patch(httpOptions).then(
          (response: HttpResponse) => response.data as T
        )
      );
    } else {
      return this.http.patch(url, body, options) as Observable<T>;
    }
  }
}
